import { isValid } from 'date-fns';

import type { TimePickerProps } from './TimePicker';
import type { TimePickerProps as DsTimePickerProps } from 'components/TimePicker/types';

export type ToDsPropsParams = Pick<
  TimePickerProps,
  'error' | 'fullWidth' | 'hint' | 'label' | 'minTime' | 'placeholder'
>;

export type TimePickerDsProps = Pick<DsTimePickerProps, 'error' | 'helperText' | 'label' | 'min'>;

/**
 * Maps the legacy MUI-X `TimePicker` props onto the Design System 2.0 `TimePicker` API, or refuses.
 *
 * TODO(HNS-264): delete this mapper and collapse the wrapper when the refresh flag retires.
 *
 * ── Two differences this mapping accepts rather than refuses ───────────────────────────────────
 *
 * Both are the refreshed component's own design and neither is expressible as a prop, so refusing
 * on them would mean this field never refreshes at all. Both are also the first thing anyone
 * opening the field sees, which is the distinction that matters - a dropped `sx` is invisible at
 * review time, these are not.
 *
 * The SLOT LABELS gain a space: `09:30 AM` where the legacy list reads `09:30AM`. The refreshed
 * field owns one format for both parsing and display, and `hh:mm a` is the spelling `date-fns` can
 * round-trip; the legacy form was reached by concatenating strings by hand rather than by choosing
 * it. A typed `09:30AM` is still accepted. See `TIME_PICKER_FORMAT`.
 *
 * The CLEAR BUTTON is renamed rather than dropped. The legacy field renders an `IconButton` with
 * no `aria-label`, so a screen reader announces it as a bare "button" - pinned as a fact in the
 * flag-off baseline precisely so the refreshed field is not held to that bar. The refreshed one
 * names it after the field ("Clear start time").
 *
 * ── What is NOT mapped, and why that is safe here ─────────────────────────────────────────────
 *
 * `onOpen` / `onClose` reach the refreshed field through `onOpenChange`, so they survive. `onAccept`
 * fires alongside `onChange`, as it does on the date wrapper. Nothing else on the legacy API is
 * silently dropped: every remaining prop either maps or refuses below.
 */
export const toDsProps = ({
  error,
  fullWidth,
  hint,
  label,
  minTime,
  placeholder,
}: ToDsPropsParams): TimePickerDsProps | null => {
  // The DS field is `w-full`; an MUI field without `fullWidth` has an intrinsic width of its own,
  // so mapping it would stretch the field and reflow its neighbours. The legacy wrapper defaults
  // this to `true`, so only a caller opting out lands here. Same call as the date wrapper's.
  if (fullWidth === false) {
    return null;
  }

  // The refreshed field's placeholder IS its format hint (`HH:MM AM`, shown only on focus), so a
  // caller who wrote their own wording would simply not get it. The legacy wrapper has no
  // placeholder default of its own, so any value here is a caller's deliberate choice.
  if (placeholder !== undefined) {
    return null;
  }

  /**
   * An unusable minimum. The legacy field guards this itself - it falls back to `startOfDay(new
   * Date())` when `minTime` is not a valid date - whereas `minutesOfDay` would return `NaN`, no
   * slot would compare selectable against it, and the user would be handed an empty list with no
   * indication why. Refusing hands the case back to the implementation that already copes.
   */
  if (minTime !== undefined && !isValid(minTime)) {
    return null;
  }

  /**
   * No label, no mapping - the position `Inputs/TextField` and `Inputs/DatePicker` both take, and
   * for the same reason: the floating label is the field's only visible name, and DS will not
   * invent one.
   *
   * This is the case the wizard's `From` and `To` fields hit. They asked for the legacy look by
   * WITHHOLDING the prop (`label={!startTime ? t('startTime') : undefined}`), which is exactly
   * what the date field beside them did - and reading that absence as "the caller wants no visible
   * name" is what shipped a filled date field with dead space where its name belonged and an
   * accessible name that changed on fill. The look is now asked for explicitly, via
   * `hideLabelWhenFilled`; see `TimePicker.tsx` and the longer note in the date wrapper's mapper.
   */
  if (label === undefined) {
    return null;
  }

  return { label, helperText: hint, error, min: minTime };
};
